import React, { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Heart, ShoppingBag } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { useWishlist } from "@/context/WishlistContext";
import { toast } from "sonner";
import QuickView from "@/components/QuickView";
import { extractSwatches } from "@/lib/swatches";
import { useImageBg } from "@/lib/imageBg";

function ShopCardActions({ product, onQuickView, onAdd }) {
  return (
    <div className="absolute bottom-2 left-2 right-2 sm:bottom-3 sm:left-3 sm:right-3 z-10 flex items-center gap-2 sm:opacity-0 sm:translate-y-2 sm:group-hover:opacity-100 sm:group-hover:translate-y-0 sm:focus-within:opacity-100 sm:focus-within:translate-y-0 transition-[opacity,transform] duration-300">
      <button
        onClick={onQuickView}
        data-testid={`quickview-btn-${product.id}`}
        className="flex-1 min-w-0 h-11 rounded-full bg-[#F4EFEA]/90 backdrop-blur-md text-[10px] tracking-[0.12em] uppercase font-sans font-medium text-[#333333] hover:bg-[#EDE6DF] transition-colors whitespace-nowrap overflow-hidden px-2"
      >
        Quick view
      </button>
      <button
        onClick={onAdd}
        data-testid={`quick-add-${product.id}`}
        aria-label="Add to bag"
        className="flex-shrink-0 h-11 min-w-[44px] px-3 rounded-full bg-[#111111] text-white inline-flex items-center justify-center gap-1.5 text-[10px] tracking-[0.15em] uppercase font-sans font-medium hover:bg-[#333333] transition-colors"
      >
        <ShoppingBag className="w-4 h-4" strokeWidth={1.75} />
        <span className="pc-add-label whitespace-nowrap">Add to bag</span>
      </button>
    </div>
  );
}

export default function ProductCard({ product, index = 0, isBestseller = false, contain = false, shop = false }) {
  const { addItem } = useCart();
  const { has, toggle } = useWishlist();
  const wished = has(product.id);
  const defaultVariant = product.variants && product.variants[0];
  const [quickOpen, setQuickOpen] = useState(false);
  const swatches = extractSwatches(product);
  const [activeSwatch, setActiveSwatch] = useState(null);
  const displayImage = activeSwatch?.image || product.image;
  const secondImage = product.images?.[1] || null;
  const main = useImageBg(contain ? displayImage : null);
  const hover = useImageBg(contain ? secondImage : null);
  const photoBg = main.bg || "#F7F7F7";
  const isSquare = (ratio) => ratio && Math.abs(ratio - 1) < 0.04;
  const fitFor = (ratio) => (isSquare(ratio) ? "inset-0 w-full h-full object-cover" : "inset-0 m-auto max-w-full max-h-full w-auto h-auto");
  const edgeFade = (ratio) => {
    if (!ratio || isSquare(ratio)) return undefined;
    const g = `linear-gradient(${ratio > 1 ? "to right" : "to bottom"}, transparent 0, #000 9%, #000 91%, transparent 100%)`;
    return { WebkitMaskImage: g, maskImage: g };
  };
  const frameCls = contain ? "aspect-square" : shop ? "pc-frame rounded-2xl bg-white aspect-[3/4]" : "bg-[#F0F0EE] aspect-[3/4]";
  const imgFit = contain ? `absolute ${fitFor(main.ratio)}` : "w-full h-full object-cover";
  const hoverFit = contain ? fitFor(hover.ratio) : "inset-0 w-full h-full object-cover";

  const openQuickView = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setQuickOpen(true);
  };

  const quickAdd = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if ((product.variants?.length || 0) > 1) return setQuickOpen(true);
    if (!defaultVariant || defaultVariant.available === false) return toast.error("Sold out");
    addItem(product, defaultVariant, 1);
  };

  const toggleWish = (e) => {
    e.preventDefault();
    e.stopPropagation();
    toggle(product.id);
    toast.success(wished ? "Removed from wishlist" : "Saved to wishlist");
  };

  return (
    <>
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-80px" }}
        transition={{ duration: 0.5, delay: (index % 8) * 0.05 }}
        data-testid={`product-card-${product.id}`}
        className={contain ? "h-full" : undefined}
      >
        <Link
          to={`/product/${product.id}`}
          className={contain ? "group h-full flex flex-col rounded-lg overflow-hidden bg-white" : "block group"}
          data-testid={contain ? `home-product-tile-${product.id}` : undefined}
        >
          <div
            className={`relative overflow-hidden ${frameCls}`}
            style={contain ? { backgroundColor: photoBg } : undefined}
            data-testid={contain ? `home-product-photo-${product.id}` : undefined}
          >
            {displayImage ? (
              <>
                <img
                  src={displayImage}
                  alt={product.name}
                  className={`${imgFit} transition-all duration-500 ${secondImage ? "group-hover:opacity-0" : "group-hover:scale-105"}`}
                  style={contain ? edgeFade(main.ratio) : undefined}
                  loading="lazy"
                  decoding="async"
                />
                {secondImage && (
                  <img
                    src={secondImage}
                    alt={`${product.name} — hover`}
                    className={`absolute ${hoverFit} opacity-0 group-hover:opacity-100 transition-opacity duration-500`}
                    style={contain ? { backgroundColor: hover.bg || photoBg, ...edgeFade(hover.ratio) } : undefined}
                    loading="lazy"
                    decoding="async"
                  />
                )}
              </>
            ) : (
              <div className="w-full h-full flex items-center justify-center text-neutral-400 font-serif italic">No image</div>
            )}
            {/* Yellow BEST SELLER badge */}
            {isBestseller && (
              <span className="absolute top-3 left-3 text-[9px] tracking-[0.15em] uppercase bg-[#F5D060] text-[#333333] px-2.5 py-1 font-sans font-semibold" data-testid={`badge-bestseller-${product.id}`}>
                Best seller
              </span>
            )}
            {!isBestseller && product.variants?.[0]?.compareAtPrice && parseFloat(product.variants[0].compareAtPrice) > product.price && (
              <span className="absolute top-3 left-3 text-[9px] tracking-[0.15em] uppercase bg-red-500 text-white px-2.5 py-1 font-sans font-semibold" data-testid={`badge-sale-${product.id}`}>
                Sale
              </span>
            )}
            {product.editors_pick && !isBestseller && (
              <span className="absolute top-3 left-3 text-[9px] tracking-[0.15em] uppercase bg-[#F5D060] text-[#333333] px-2.5 py-1 font-sans font-semibold">
                Editor's Pick
              </span>
            )}
            {/* Wishlist heart — top right, subtle */}
            <button
              onClick={toggleWish}
              data-testid={`wishlist-${product.id}`}
              aria-label="Save to wishlist"
              className="absolute top-1 right-1 z-10 w-11 h-11 flex items-center justify-center sm:opacity-0 sm:group-hover:opacity-100 transition-opacity duration-200"
            >
              <Heart className={`w-5 h-5 ${wished ? "fill-[#333333] stroke-[#333333]" : "stroke-[#333333]/60"}`} strokeWidth={1.5} />
            </button>
            {/* QUICK VIEW (+ Add to bag on shop pages) on hover */}
            {shop ? (
              <ShopCardActions product={product} onQuickView={openQuickView} onAdd={quickAdd} />
            ) : (
              <button
                onClick={openQuickView}
                data-testid={`quickview-btn-${product.id}`}
                className={`absolute bottom-3 left-3 right-3 z-10 bg-white/70 backdrop-blur-md min-h-[44px] py-3 text-center text-[10px] tracking-[0.25em] uppercase font-sans font-medium text-[#333333] rounded-xl sm:opacity-0 sm:translate-y-2 sm:group-hover:opacity-100 sm:group-hover:translate-y-0 transition-all duration-300 hover:bg-white/90 border border-white/40 ${contain ? "hidden sm:block" : ""}`}
              >
                Quick view
              </button>
            )}
          </div>
          <div className={contain ? "flex-1 px-3.5 sm:px-5 pt-3 sm:pt-4 pb-4 sm:pb-5 space-y-1" : "mt-4 space-y-0.5"}>
            <p className={contain ? "text-[11px] sm:text-xs tracking-[0.08em] uppercase text-[#111111] font-sans font-bold" : "text-[10px] tracking-[0.2em] uppercase text-neutral-400 font-sans"}>{(product.brand === "SELFNAMED" || product.brand === "Selfnamed") ? "DORIA" : product.brand}</p>
            <h3 className={`leading-snug line-clamp-2 ${contain ? "font-sans text-[15px] sm:text-lg text-[#111111] min-h-[2.75em]" : shop ? "font-serif text-[17px] sm:text-lg pt-0.5 text-[#333333]" : "font-sans text-sm sm:text-[15px] text-[#333333]"}`}>{product.name}</h3>
            {(() => {
              const v = product.variants?.[0];
              const compareAt = v?.compareAtPrice;
              const hasDiscount = compareAt && parseFloat(compareAt) > product.price;
              return hasDiscount ? (
                <div className="flex items-center gap-2 pt-0.5">
                  <span className={`font-sans text-red-600 ${contain ? "text-[15px] sm:text-[17px]" : "text-sm"} ${shop ? "font-semibold" : "font-medium"}`}>${product.price.toFixed(2)}</span>
                  <span className={`font-sans text-neutral-400 line-through ${contain ? "text-[13px] sm:text-[15px]" : "text-sm"}`}>${parseFloat(compareAt).toFixed(2)}</span>
                </div>
              ) : (
                <p className={`font-sans pt-0.5 ${contain ? "text-[15px] sm:text-[17px] text-[#111111]" : "text-sm text-[#333333]"} ${shop ? "font-semibold" : ""}`}>${product.price.toFixed(2)}</p>
              );
            })()}
            {swatches.length > 1 && (
              <div className="flex items-center gap-1.5 pt-2" data-testid={`swatches-${product.id}`}>
                {swatches.slice(0, 6).map((s) => (
                  <button
                    key={s.value}
                    onMouseEnter={() => setActiveSwatch(s)}
                    onFocus={() => setActiveSwatch(s)}
                    onClick={(e) => { e.preventDefault(); e.stopPropagation(); setActiveSwatch(s); }}
                    onMouseLeave={() => setActiveSwatch(null)}
                    onBlur={() => setActiveSwatch(null)}
                    data-testid={`swatch-${product.id}-${s.value}`}
                    aria-label={`Preview ${s.value}`}
                    title={s.value}
                    className={`w-4 h-4 rounded-full border transition-transform ${activeSwatch?.value === s.value ? "border-[#333333] scale-125" : "border-[#333333]/20 hover:scale-110"}`}
                    style={{ background: s.hex || `linear-gradient(135deg, #d9c0a5, #7a5a48)` }}
                  />
                ))}
                {swatches.length > 6 && (
                  <span className="text-[10px] text-neutral-400 font-sans ml-1">+{swatches.length - 6}</span>
                )}
              </div>
            )}
          </div>
        </Link>
      </motion.div>
      {quickOpen && <QuickView product={product} onClose={() => setQuickOpen(false)} />}
    </>
  );
}
